import { ref } from 'vue'
import { apiFetch } from './api'

const cachedBaseUrl = ref<string | null>(null)
let fetchPromise: Promise<string | null> = null

/**
 * Fetch and cache the Salesforce base URL from the backend.
 * @returns {Promise<string|null>}
 */
async function getSalesforceBaseUrl(): Promise<string | null> {
  if (cachedBaseUrl.value !== null) {
    return cachedBaseUrl.value
  }

  if (fetchPromise) {
    return fetchPromise
  }

  fetchPromise = apiFetch('/settings/integrations/salesforce/base_url')
    .then(resp => {
      cachedBaseUrl.value = resp?.base_url || null
      fetchPromise = null
      return cachedBaseUrl.value
    })
    .catch(() => {
      fetchPromise = null
      return null
    })

  return fetchPromise
}

export async function preloadSalesforceBaseUrl(): Promise<void> {
  await getSalesforceBaseUrl()
}

/**
 * Get the Salesforce Lightning URL for any Salesforce object.
 * Uses a reactive cached base URL so Vue computed properties
 * automatically re-evaluate once the base URL finishes loading.
 *
 * @param {Object} sfObject - Salesforce object with Id field
 * @param {string} objectType - Type of object (e.g., 'Opportunity', 'Contact', 'Account')
 * @returns {string|null} - Full Salesforce Lightning URL or null
 */
export function getSalesforceUrl(sfObject: Record<string, any>, objectType: string): string | null {
  if (!sfObject || !objectType) return null

  if (!cachedBaseUrl.value) return null

  const objectId = sfObject?.Id || sfObject?.id
  if (!objectId) return null

  return `${cachedBaseUrl.value}/lightning/r/${objectType}/${objectId}/view`
}
